/**
 * Helper functions for building patient and billing party data
 * Used across all booking components for consistent data handling
 */

/**
 * Builds the patient object for submission
 * When booking for self: uses patient's email/phone
 * When booking for other: uses responsible party's email/phone for contact
 *
 * @param {string} isResponsibleParty - 'self' or 'other'
 * @param {object} values - Form values containing patient and responsibleParty data
 * @returns {object} Patient data for submission
 */
export const buildPatient = (isResponsibleParty, values) => {
  // Handle flat form structure (scheduleconnect/manual booking)
  if (!values.patient) {
    return {
      firstName: values.firstName,
      lastName: values.lastName,
      email:
        isResponsibleParty === 'self'
          ? values.email
          : values.responsibleParty?.email || values.email,
      phone:
        isResponsibleParty === 'self'
          ? values.phone
          : values.responsibleParty?.phone || values.phone,
      dob: values.dob,
      insuranceInfo: values.insuranceInfo,
      comment: values.comment,
    }
  }

  // Handle nested form structure (dolphin booking)
  return {
    firstName: values.patient.firstName,
    lastName: values.patient.lastName,
    email:
      isResponsibleParty === 'self'
        ? values.patient.email
        : values.responsibleParty.email,
    phone:
      isResponsibleParty === 'self'
        ? values.patient.phone
        : values.responsibleParty.phone,
    dob: values.patient.dob,
    insuranceInfo: values.patient.insuranceInfo,
    comment: values.patient.comment,
  }
}

/**
 * Builds the billing/responsible party object for submission
 * When booking for self: billing party = patient info
 * When booking for other: billing party = responsible party info
 *
 * @param {string} isResponsibleParty - 'self' or 'other'
 * @param {object} values - Form values containing patient and responsibleParty data
 * @returns {object} Billing party data for submission
 */
export const buildBillingParty = (isResponsibleParty, values) => {
  // Handle flat form structure (scheduleconnect/manual booking)
  if (!values.patient) {
    if (isResponsibleParty === 'self') {
      return {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        dob: values.dob,
      }
    }
    return {
      firstName: values.responsibleParty?.firstName || '',
      lastName: values.responsibleParty?.lastName || '',
      email: values.responsibleParty?.email || '',
      phone: values.responsibleParty?.phone || '',
      dob: values.responsibleParty?.dob || '',
    }
  }

  // Handle nested form structure (dolphin booking)
  return {
    firstName:
      isResponsibleParty === 'self'
        ? values.patient.firstName
        : values.responsibleParty.firstName,
    lastName:
      isResponsibleParty === 'self'
        ? values.patient.lastName
        : values.responsibleParty.lastName,
    email:
      isResponsibleParty === 'self'
        ? values.patient.email
        : values.responsibleParty.email,
    phone:
      isResponsibleParty === 'self'
        ? values.patient.phone
        : values.responsibleParty.phone,
    dob:
      isResponsibleParty === 'self'
        ? values.patient.dob
        : values.responsibleParty.dob,
  }
}
